/* eslint-disable react-refresh/only-export-components */
import { useState, createContext, useContext, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import axios from '@/api/api.js'
import { getAllApplParameters } from '@/api/services/adminServices'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionTimeout, setSessionTimeout] = useState(30) // Default 30 mins
  const [specApprovalLevel, setSpecApprovalLevel] = useState('D') // Default Double Approval (D)
  const timerRef = useRef(null)

  const logout = useCallback(() => {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('refreshToken')
    setUser(null)
    setIsAuthenticated(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    console.log('🔐 AuthContext: User logged out.')
  }, [])

  const checkAuth = useCallback(() => {
    console.log('checking auth . . .')
    const token = sessionStorage.getItem('token')
    const userData = sessionStorage.getItem('user')

    if (token) {
      setIsAuthenticated(true)
      // Debug: Verify JWT claims for 500 error diagnosis
      try {
        const parts = token.split('.')
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1]))
          console.log('🔐 AuthContext Check - JWT Claims:', {
            userId: payload.userId || payload.TOKEN_USER_ID,
            auditId: payload.auditId || payload.TOKEN_AUDIT_ID,
            aud: payload.aud
          })
        }
      } catch (e) {
        console.warn('Could not parse token claims for debug', e)
      }
      if (userData) {
        try {
          setUser(JSON.parse(userData))
        } catch (error) {
          console.error('Error parsing user data', error)
        }
      }
    } else {
      console.log('no user data found')
    }
    setLoading(false)
  }, [])

  // Inactivity Timer Logic
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return
    
    if (timerRef.current) clearTimeout(timerRef.current)
    
    // Convert minutes to milliseconds
    const timeoutMs = sessionTimeout * 60 * 1000
    
    timerRef.current = setTimeout(() => {
      console.warn(`⚠️ Session expired after ${sessionTimeout} minutes of inactivity.`)
      logout()
    }, timeoutMs)
  }, [isAuthenticated, sessionTimeout, logout])

  // Activity Listeners
  useEffect(() => {
    if (isAuthenticated) {
      const activities = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
      
      activities.forEach(event => {
        window.addEventListener(event, resetInactivityTimer)
      })
      
      resetInactivityTimer() // Initial start
      
      return () => {
        activities.forEach(event => {
          activities.forEach(event => {
            window.removeEventListener(event, resetInactivityTimer)
          })
          if (timerRef.current) clearTimeout(timerRef.current)
        })
      }
    }
  }, [isAuthenticated, resetInactivityTimer])

  const fetchApplicationParameters = useCallback(async () => {
    try {
      // Use the service directly instead of dynamic import for stability
      const params = await getAllApplParameters()
      if (params && Array.isArray(params)) {
        // Fetch Session Timeout (SYSTEM LOG OFF in legacy, SYSTEM LOCK OUT in some contexts)
        const timeoutParam = params.find(p => p.parameterName === 'SYSTEM LOG OFF' || p.parameterName === 'SYSTEM LOCK OUT')
        if (timeoutParam && timeoutParam.parameterValue) {
          const mins = parseInt(timeoutParam.parameterValue, 10)
          if (!isNaN(mins) && mins > 0) {
            setSessionTimeout(mins)
            console.log(`🔐 Session Timeout set to: ${mins} minutes.`)
          }
        }

        // Fetch Specification Approval Level
        const approvalLevelParam = params.find(
          p => p.parameterName === 'SPECIFICATION APPROVAL LEVEL'
        )
        if (approvalLevelParam && approvalLevelParam.parameterValue) {
          console.log('✅ Specification Approval Level set to:', approvalLevelParam.parameterValue)
          setSpecApprovalLevel(approvalLevelParam.parameterValue)
        }
      }
    } catch (error) {
      console.warn('Could not fetch application parameters, using defaults', error)
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplicationParameters()
    }
  }, [isAuthenticated, fetchApplicationParameters])

  const login = (token, userData) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    sessionStorage.setItem('token', token)
    // Debug: Verify JWT claims for 500 error diagnosis
    try {
      const parts = token.split('.')
      if (parts.length >= 2) {
        const payload = JSON.parse(atob(parts[1]))
        console.log('🔐 AuthContext Login - JWT Claims:', {
          userId: payload.userId || payload.TOKEN_USER_ID,
          auditId: payload.auditId || payload.TOKEN_AUDIT_ID
        })
      }
    } catch (e) {
      console.warn('Could not parse token claims for debug', e)
    }
    if (userData) {
      sessionStorage.setItem('user', JSON.stringify(userData))
      setUser(userData)
    }
    setIsAuthenticated(true)
  }

  const isTokenExpired = () => {
    const token = sessionStorage.getItem('token')
    if (!token) return true

    // For development mock tokens, don't treat them as expired JWTs
    if (token === 'mock-jwt-token-for-development' || !token.includes('.')) {
      return false
    }

    try {
      const parts = token.split('.')
      if (parts.length < 2) return false

      const payload = JSON.parse(atob(parts[1]))
      return payload.exp < Date.now() / 1000
    } catch (error) {
      console.error('Error checking token expiration', error)
      return false // Error parsing? Assume it's okay for now rather than forcing logout
    }
  }

  const refreshToken = async () => {
    try {
      const response = await fetch('/api/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: sessionStorage.getItem('refreshToken') })
      })
      const data = await response.json()
      if (data.token) {
        sessionStorage.setItem('token', data.token)
        return true
      } else {
        throw { message: 'Token Data not found' }
      }
    } catch (error) {
      console.error('Error refreshing token', error)
      return false
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        setLoading,
        loading,
        login,
        logout,
        isTokenExpired,
        refreshToken,
        sessionTimeout,
        specApprovalLevel,
        fetchApplicationParameters
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export const useAuth = () => useContext(AuthContext)
